/**
 * SkillSelector
 * Component for selecting skills to attach to an assistant
 */

import React, { useState, useEffect } from 'react';
import { Checkbox, Badge } from '../../ui';
import { skillsApi } from '../../../services/api';
import type { Skill } from '../../../types';
import styles from './SkillSelector.module.css';

export interface SkillSelectorProps {
  selectedSkillIds: string[];
  onSelectionChange: (skillIds: string[]) => void;
}

export const SkillSelector: React.FC<SkillSelectorProps> = ({
  selectedSkillIds,
  onSelectionChange,
}) => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSkills();
  }, []);

  const loadSkills = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await skillsApi.list();
      if (response.success && response.data) {
        setSkills(response.data.skills || []);
      }
    } catch (err) {
      console.error('Failed to load skills:', err);
      setError('Failed to load skills. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSkill = (skillId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedSkillIds, skillId]);
    } else {
      onSelectionChange(selectedSkillIds.filter((id) => id !== skillId));
    }
  };

  if (isLoading) {
    return <div className={styles.loading}>Loading skills...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (skills.length === 0) {
    return (
      <div className={styles.empty}>
        No skills available. Create a skill first to attach it to this assistant.
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.skillsList}>
        {skills.map((skill) => (
          <div key={skill.id} className={styles.skillItem}>
            <div className={styles.skillCheckbox}>
              <Checkbox
                checked={selectedSkillIds.includes(skill.id)}
                onChange={(checked) => handleToggleSkill(skill.id, checked)}
              />
            </div>
            <div className={styles.skillInfo}>
              <div className={styles.skillHeader}>
                <div className={styles.skillName}>{skill.displayName}</div>
                {skill.categories && skill.categories.length > 0 && (
                  <div className={styles.skillCategories}>
                    {skill.categories.slice(0, 2).map((category) => (
                      <Badge key={category} variant="default" size="sm">
                        {category}
                      </Badge>
                    ))}
                    {skill.categories.length > 2 && (
                      <Badge variant="default" size="sm">
                        +{skill.categories.length - 2}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <p className={styles.skillDescription}>
                {skill.description.length > 100
                  ? `${skill.description.substring(0, 100)}...`
                  : skill.description}
              </p>
            </div>
          </div>
        ))}
      </div>
      {selectedSkillIds.length > 0 && (
        <div className={styles.summary}>
          {selectedSkillIds.length} {selectedSkillIds.length === 1 ? 'skill' : 'skills'} selected
        </div>
      )}
    </div>
  );
};

export default SkillSelector;
